import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Button, Card, Select, Space, Table, Tag } from 'antd';

import BulkStatusTag from './BulkStatusTag';
import BulkUploadDetail from './BulkUploadDetail';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import { BULK_UPLOADS } from 'queries/bulkOperations';
import { formatDateTime } from 'utilities/datesAndTimes';
import {
  ACTION_LABEL,
  CUSTOMER_ATTRIBUTE_LABEL,
  HISTORY_PAGE_SIZE,
  UPLOAD_STATUS,
  asOptions,
} from './constants';

/** The status pair or the attributes, whichever qualifies the action. */
const buildQualifierLabel = ({ statusCode, reasonCode, attributes }) => {
  if (statusCode) {
    return `${statusCode}${reasonCode ?? ''}`;
  }
  if (attributes?.length) {
    return attributes
      .map((attribute) => CUSTOMER_ATTRIBUTE_LABEL[attribute] ?? attribute)
      .join(', ');
  }
  return '';
};

const columns = (onOpen) => [
  {
    title: 'Uploaded',
    dataIndex: 'insertTz',
    key: 'insertTz',
    render: (insertTz) => formatDateTime(insertTz, true),
  },
  {
    title: 'Label',
    dataIndex: 'label',
    key: 'label',
    render: (label) => label || '—',
  },
  { title: 'File', dataIndex: 'filename', key: 'filename' },
  {
    title: 'Actions',
    dataIndex: 'requestedActions',
    key: 'requestedActions',
    render: (actions) => (
      <Space size={[0, 4]} wrap>
        {actions.map((action) => {
          const qualifierLabel = buildQualifierLabel(action);
          return (
            <Tag key={`${action.type}-${qualifierLabel}`}>
              {ACTION_LABEL[action.type] ?? action.type}
              {qualifierLabel ? ` (${qualifierLabel})` : ''}
            </Tag>
          );
        })}
      </Space>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status) => <BulkStatusTag value={status} labels={UPLOAD_STATUS} />,
  },
  {
    key: 'details',
    align: 'right',
    render: (_, upload) => (
      <Button type="link" onClick={() => onOpen(upload.uploadToken)}>
        Details
      </Button>
    ),
  },
];

const BulkUploadHistory = () => {
  const [status, setStatus] = useState();
  const [page, setPage] = useState(0);
  const [openToken, setOpenToken] = useState();

  const uploadsQuery = useQuery(BULK_UPLOADS, {
    variables: { status, page, size: HISTORY_PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  const uploads =
    uploadsQuery.data?.bulkUploads ?? uploadsQuery.previousData?.bulkUploads;

  /**
   * The boundary owns the first load and the error. Once there is a page to show
   * the table keeps it under its own spinner, because the boundary's skeleton
   * replaces the pager too and paging away from it needs the pager.
   */
  const boundary = uploads
    ? { ...uploadsQuery, data: uploads, loading: false, dataState: 'complete' }
    : uploadsQuery;

  const filterBy = (next) => {
    setStatus(next);
    // Page 3 of everything is rarely page 3 of one status, and is often past the
    // end of it.
    setPage(0);
  };

  return (
    <Card
      title="Your uploads"
      style={{ marginTop: 'var(--spacing-lg)' }}
      extra={
        <Select
          allowClear
          placeholder="All statuses"
          aria-label="Filter by status"
          style={{ minWidth: 180 }}
          value={status}
          onChange={filterBy}
          options={asOptions(UPLOAD_STATUS)}
        />
      }
    >
      <QueryBoundary
        query={boundary}
        handlePartialSuccess={false}
        loadingMessage="Loading your uploads"
        errorMessage="Failed to load your uploads"
      >
        <Table
          size="small"
          rowKey="uploadToken"
          loading={uploadsQuery.loading}
          dataSource={uploads?.content ?? []}
          columns={columns(setOpenToken)}
          locale={{ emptyText: 'You have not uploaded anything yet.' }}
          pagination={{
            // antd counts pages from one; the service counts from zero.
            current: page + 1,
            pageSize: HISTORY_PAGE_SIZE,
            total: uploads?.totalElements ?? 0,
            showSizeChanger: false,
            onChange: (next) => setPage(next - 1),
          }}
        />
      </QueryBoundary>

      <BulkUploadDetail
        uploadToken={openToken}
        onClose={() => setOpenToken(undefined)}
      />
    </Card>
  );
};

export default BulkUploadHistory;
